import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TriangleAlert, Trash } from "lucide-react";
import { EVIDENCE_RELATION, EVIDENCE_STATE, RESULT_OUTCOME, extractInternalLinks } from "@brainlog/shared";
import { del, errorMessage, get } from "@/lib/api";
import { openNote, openPaper, openView, type Tab } from "@/lib/store";
import { useNote } from "@/lib/queries";
import type { Task } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DiffView } from "@/components/DiffView";
import { useResolvedLinks, InternalLink } from "@/features/notes/MarkdownPreview";
import { KindIcon } from "@/features/notes/kinds";
import { FileLinksPanel } from "@/features/files/FileLinksPanel";
import { useTargetFiles } from "@/features/files/useFiles";

interface EvidenceView {
  id: string;
  relation: string;
  explanation: string;
  claimQuote: string;
  claimStillPresent: boolean | null;
  sourceType: string;
  sourceId: string;
  sourceTitle: string | null;
  sourceAccessible: boolean;
  sourceKind: string | null;
  sourcePage: number | null;
  sourcePageLabel: string | null;
  sourceSection: string | null;
  sourceQuote: string;
  sourceChanged: boolean;
  sourceChangeDetail: string | null;
  sourceQuoteFound: boolean | null;
  evidenceState: string | null;
  resultOutcome: string | null;
  redirectedTo: string | null;
  citedVersionNo: number | null;
  createdBy: string | null;
}

const relLabel = (r: string) => (EVIDENCE_RELATION.labels as Record<string, string>)[r] ?? r;

function Section({ title, children, count }: { title: string; children: React.ReactNode; count?: number }) {
  return (
    <section className="border-b px-3 py-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
        {count !== undefined && ` (${count})`}
      </h3>
      {children}
    </section>
  );
}

export function SourcesPanel({ tab }: { tab: Tab | null }) {
  if (!tab) return <p className="p-3 text-sm text-muted-foreground">Open a note or paper to see its sources and links.</p>;
  if (tab.kind === "note" && tab.id) return <NoteSources noteId={tab.id} />;
  if (tab.kind === "paper" && tab.id) return <PaperSources itemId={tab.id} />;
  return <p className="p-3 text-sm text-muted-foreground">Sources are shown for notes and papers.</p>;
}

function NoteSources({ noteId }: { noteId: string }) {
  const qc = useQueryClient();
  const note = useNote(noteId);
  const evidence = useQuery({ queryKey: ["evidence", noteId], queryFn: () => get<{ asClaim: EvidenceView[]; asSource: any[] }>(`/api/notes/${noteId}/evidence`) });
  const backlinks = useQuery({ queryKey: ["backlinks", noteId], queryFn: () => get<{ backlinks: any[]; recordLinks: any[] }>(`/api/notes/${noteId}/backlinks`) });
  const wsId = note.data?.note.workspaceId;
  const tasks = useQuery({
    queryKey: ["tasks", "source", noteId],
    queryFn: () => get<{ tasks: Task[] }>(`/api/tasks?workspaceId=${wsId}&sourceId=${noteId}`),
    enabled: !!wsId,
  });
  const files = useTargetFiles("note", noteId);
  const text = note.data?.note.contentMd ?? "";
  const outgoing = extractInternalLinks(text);
  const resolved = useResolvedLinks(text);
  const [review, setReview] = useState<string | null>(null);

  return (
    <div>
      <Section title="Evidence for claims in this note" count={evidence.data?.asClaim.length}>
        {evidence.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        <ul className="space-y-2">
          {evidence.data?.asClaim.map((e) => (
            <li key={e.id} className="rounded-md border bg-background p-2 text-sm">
              <p className="text-xs font-semibold uppercase tracking-wide">{relLabel(e.relation)}</p>
              <p className="mt-0.5">
                <span className="text-muted-foreground">Claim:</span> “{e.claimQuote}”
              </p>
              <p className="mt-0.5">
                <span className="text-muted-foreground">Source:</span>{" "}
                {e.sourceAccessible ? (
                  <button
                    type="button"
                    className="underline"
                    onClick={() => (e.sourceType === "note" ? openNote(e.sourceId, e.sourceTitle ?? "Record") : openPaper(e.redirectedTo ?? e.sourceId, e.sourceTitle ?? "Paper", e.sourcePage ?? undefined))}
                  >
                    {e.sourceTitle}
                  </button>
                ) : (
                  "Not accessible to you"
                )}
                {e.sourcePage && ` — PDF page ${e.sourcePage}${e.sourcePageLabel ? ` (printed ${e.sourcePageLabel})` : ""}`}
                {e.sourceSection && ` — ${e.sourceSection}`}
                {e.citedVersionNo && ` — cited version v${e.citedVersionNo}`}
              </p>
              {e.sourceQuote && <blockquote className="mt-1 border-l-2 pl-2 text-xs italic">{e.sourceQuote}</blockquote>}
              {e.explanation && <p className="mt-1 text-xs">{e.explanation}</p>}
              {(e.evidenceState || e.resultOutcome) && (
                <p className="mt-1 text-xs">
                  Source result recorded as{" "}
                  <strong>{e.evidenceState ? (EVIDENCE_STATE.labels as Record<string, string>)[e.evidenceState] : ""}</strong>
                  {e.resultOutcome && ` · ${(RESULT_OUTCOME.labels as Record<string, string>)[e.resultOutcome]}`}
                </p>
              )}
              {(e.sourceChanged || e.claimStillPresent === false || e.sourceQuoteFound === false) && (
                <div className="mt-1 space-y-0.5 rounded border border-warning/50 bg-warning/10 p-1.5 text-xs" role="status">
                  {e.sourceChanged && (
                    <p className="flex gap-1">
                      <TriangleAlert className="size-3.5 shrink-0" aria-hidden /> Source changed: {e.sourceChangeDetail}
                    </p>
                  )}
                  {e.sourceQuoteFound === false && <p>The quoted passage is no longer found in the source.</p>}
                  {e.claimStillPresent === false && <p>The claim text was edited since the link was made.</p>}
                </div>
              )}
              <div className="mt-1.5 flex gap-1">
                <Button size="xs" variant="outline" onClick={() => setReview(e.id)}>
                  Review against cited version
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  aria-label="Remove evidence link"
                  onClick={async () => {
                    try {
                      await del(`/api/evidence-links/${e.id}`);
                      qc.invalidateQueries({ queryKey: ["evidence", noteId] });
                    } catch (err) {
                      toast.error(errorMessage(err));
                    }
                  }}
                >
                  <Trash />
                </Button>
              </div>
            </li>
          ))}
          {evidence.data && !evidence.data.asClaim.length && <li className="text-sm text-muted-foreground">No evidence links. Select a claim and use “Add evidence link”.</li>}
        </ul>
      </Section>
      {(evidence.data?.asSource.length ?? 0) > 0 && (
        <Section title="Cited as evidence by" count={evidence.data!.asSource.length}>
          <ul className="space-y-1 text-sm">
            {evidence.data!.asSource.map((e) => (
              <li key={e.id}>
                <button type="button" className="underline" onClick={() => openNote(e.claimNoteId, e.claimTitle)}>
                  {e.claimTitle}
                </button>{" "}
                <span className="text-xs text-muted-foreground">({relLabel(e.relation)}): “{e.claimQuote}”</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title="Linked from" count={(backlinks.data?.backlinks.length ?? 0) + (backlinks.data?.recordLinks.length ?? 0)}>
        <p className="mb-1 text-xs text-muted-foreground">Only notes you can open are listed.</p>
        <ul className="space-y-1 text-sm">
          {backlinks.data?.backlinks.map((b) => (
            <li key={b.id} className="flex items-center gap-1.5">
              <KindIcon kind={b.kind} />
              <button type="button" className="truncate underline" onClick={() => openNote(b.id, b.title)}>
                {b.title}
              </button>
            </li>
          ))}
          {backlinks.data?.recordLinks.map((b) => (
            <li key={b.id} className="flex items-center gap-1.5">
              <KindIcon kind={b.kind} />
              <button type="button" className="truncate underline" onClick={() => openNote(b.fromNoteId, b.title)}>
                {b.title}
              </button>
              <span className="text-xs text-muted-foreground">({b.relation.replace(/_/g, " ")})</span>
            </li>
          ))}
          {backlinks.data && !backlinks.data.backlinks.length && !backlinks.data.recordLinks.length && <li className="text-muted-foreground">Nothing links here yet.</li>}
        </ul>
      </Section>
      <Section title="Links in this note" count={outgoing.length}>
        <ul className="space-y-1 text-sm">
          {outgoing.map((l, i) => (
            <li key={`${l.href}-${i}`}>
              <InternalLink href={l.href} resolved={resolved.data}>
                {l.label || l.href}
              </InternalLink>{" "}
              <span className="text-xs text-muted-foreground">({l.type}{l.page ? `, PDF p. ${l.page}` : ""})</span>
            </li>
          ))}
          {!outgoing.length && <li className="text-muted-foreground">No links to other items.</li>}
        </ul>
      </Section>
      <Section title="Files" count={files.data?.length}>
        {wsId && <FileLinksPanel targetType="note" targetId={noteId} workspaceId={wsId} readOnly={!!note.data?.note.deletedAt} />}
      </Section>
      <Section title="Tasks created from this note" count={tasks.data?.tasks.length}>
        <ul className="space-y-1 text-sm">
          {tasks.data?.tasks.map((t) => (
            <li key={t.id}>
              <button type="button" className="underline" onClick={() => openView("backlog", t.workspaceId, "Backlog", { task: t.id })}>
                {t.title}
              </button>{" "}
              <span className="text-xs text-muted-foreground">({t.status.replace("_", " ")})</span>
            </li>
          ))}
          {tasks.data && !tasks.data.tasks.length && <li className="text-muted-foreground">None.</li>}
        </ul>
      </Section>
      {review && <EvidenceReviewDialog id={review} onClose={() => setReview(null)} />}
    </div>
  );
}

function EvidenceReviewDialog({ id, onClose }: { id: string; onClose(): void }) {
  const r = useQuery({ queryKey: ["evidence-review", id], queryFn: () => get<any>(`/api/evidence-links/${id}/review`) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Review evidence against the cited versions</DialogTitle>
          <DialogDescription>Compare what was cited with the current text. The evidence link keeps pointing at the cited version.</DialogDescription>
        </DialogHeader>
        {!r.data && <p className="text-sm text-muted-foreground">Loading…</p>}
        {r.data && (
          <div className="space-y-4">
            <section>
              <h3 className="mb-1 text-sm font-medium">Claim note</h3>
              {r.data.claim.citedVersion ? (
                <DiffView before={r.data.claim.citedVersion.contentMd} after={r.data.claim.current} beforeLabel={`Cited version v${r.data.claim.citedVersion.versionNo}`} afterLabel="Current" />
              ) : (
                <p className="text-sm text-muted-foreground">No cited version recorded.</p>
              )}
            </section>
            <section>
              <h3 className="mb-1 text-sm font-medium">Source</h3>
              {r.data.source?.type === "note" && r.data.source.citedVersion && (
                <DiffView before={r.data.source.citedVersion.contentMd} after={r.data.source.current} beforeLabel={`Cited version v${r.data.source.citedVersion.versionNo}`} afterLabel="Current" />
              )}
              {r.data.source?.type === "note" && r.data.source.accessible === false && <p className="text-sm">The source is not accessible to you.</p>}
              {r.data.source?.type === "paper" && (
                <div className="space-y-1 text-sm">
                  <p>{r.data.source.pageChanged ? "The extracted text of the cited page changed since it was cited." : "The cited page text is unchanged since it was cited."}</p>
                  {r.data.source.pageText && <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">{r.data.source.pageText}</pre>}
                </div>
              )}
              {!r.data.source && <p className="text-sm text-muted-foreground">No page text for this source (it has no PDF, or no page was chosen).</p>}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaperSources({ itemId }: { itemId: string }) {
  const item = useQuery({ queryKey: ["library-item", itemId], queryFn: () => get<any>(`/api/library/${itemId}`) });
  if (!item.data) return <p className="p-3 text-sm text-muted-foreground">Loading…</p>;
  const d = item.data;
  return (
    <div>
      <Section title="Reading notes" count={d.readingNotes.length}>
        <ul className="space-y-1 text-sm">
          {d.readingNotes.map((n: any) => (
            <li key={n.id}>
              <button type="button" className="underline" onClick={() => openNote(n.id, n.title)}>
                {n.title}
              </button>
            </li>
          ))}
          {!d.readingNotes.length && <li className="text-muted-foreground">None yet.</li>}
        </ul>
      </Section>
      <Section title="Used as evidence" count={d.evidence.length}>
        <ul className="space-y-2 text-sm">
          {d.evidence.map((e: any) => (
            <li key={e.id} className="rounded border bg-background p-2">
              <span className="text-xs font-semibold uppercase">{relLabel(e.relation)}</span> in{" "}
              <button type="button" className="underline" onClick={() => openNote(e.claimNoteId, e.claimTitle)}>
                {e.claimTitle}
              </button>
              <p className="text-xs">“{e.claimQuote}”{e.sourcePage ? ` — PDF page ${e.sourcePage}${e.sourcePageLabel ? ` (printed ${e.sourcePageLabel})` : ""}` : ""}</p>
            </li>
          ))}
          {!d.evidence.length && <li className="text-muted-foreground">Not cited as evidence.</li>}
        </ul>
      </Section>
      <Section title="Cited or linked in notes" count={d.citingNotes.length}>
        <ul className="space-y-1 text-sm">
          {d.citingNotes.map((n: any) => (
            <li key={n.id}>
              <button type="button" className="underline" onClick={() => openNote(n.id, n.title)}>
                {n.title}
              </button>
            </li>
          ))}
          {!d.citingNotes.length && <li className="text-muted-foreground">None.</li>}
        </ul>
      </Section>
    </div>
  );
}
